import React from 'react';
import { Form } from 'react-final-form';
import { useHistory } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import {
  ModuleHeader,
  ModuleHeaderTitle,
} from '@modules/admin/_common/ModuleHeader';
import { ModuleSection } from '@modules/admin/_common/ModuleSection';
import styled from 'styled-components';

import PrimaryButton from '@common/button';
import {
  ActionType,
  Flow,
  FormState,
  useBookingContext,
} from '@common/context/BookingContext';
import ErrorMessage from '@common/fields/_common/errorMessage';
import InputField from '@common/fields/InputField';
import TextField from '@common/fields/TextField';
import useQueryParams from '@common/utils/hooks/useQueryParams';
import { useScopedTranslation } from '@common/utils/hooks/useScopedTranslation';
import { CallbackQuery, CreateCallback } from '@typings/api';

import CallbackHourField from './_common/CallbackHourField';
import Section from './_common/Section';
import SectionHeader from './_common/SectionHeader';
import { CREATE_CALLBACK } from './mutations';
import { CALLBACK } from './queries';

const SubmitButton = styled(PrimaryButton)`
  width: max-content;
`;

interface Props {
  isForced?: boolean;
  isSelfServicing?: boolean;
  contextId?: string;
}

export default function ReferralFlow({
  contextId: contextIdProp,
  isForced,
  isSelfServicing,
}: Props) {
  const { t } = useScopedTranslation();
  const { id } = useQueryParams();
  const contextId = contextIdProp ?? id;
  const history = useHistory();
  const [{ customerFormState, customerValid }, dispatch] = useBookingContext();

  const { data } = useQuery<CallbackQuery>(CALLBACK, {
    variables: { contextId },
    skip: !contextId,
  });
  const callback = data?.context?.callback;

  const [createCallback, { error, loading }] = useMutation<CreateCallback>(
    CREATE_CALLBACK,
    {
      onCompleted(data) {
        const callbackId = data?.createCallback?.callbackId;

        if (callbackId !== undefined) {
          const confirmRoute = isSelfServicing ? 'confirmCallback' : 'confirm';
          history.push(
            `/${confirmRoute}?callbackId=${callbackId}&id=${contextId}`,
          );
        }
      },
    },
  );

  function handleSubmit(values: any) {
    return createCallback({
      variables: { input: { ...values, contextId, forced: false } },
    });
  }

  function setBookingFlow() {
    return dispatch({
      type: ActionType.SET_FLOW,
      payload: Flow.BOOKING,
    });
  }

  if (callback == null) {
    return <div>{t('errors.technical')}</div>;
  }

  return (
    <>
      <ModuleHeader>
        <ModuleHeaderTitle>{t('referral_form.title')}</ModuleHeaderTitle>
      </ModuleHeader>
      <ModuleSection>
        <Form
          initialValues={{ callbackHours: null, notes: null }}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, values }) => (
            <form onSubmit={handleSubmit}>
              <CallbackHourField
                buttons={callback.buttons}
                isSelfServicing={isSelfServicing}
              />
              {(values.callbackHours !== null ||
                callback?.buttons?.length === 0) && (
                <>
                  <Section>
                    <SectionHeader>
                      {t('referral_form.notes.question')}
                    </SectionHeader>
                    <InputField
                      label={t('referral_form.notes.label')}
                      name="notes"
                    >
                      {(input) => <TextField {...input} type="text" />}
                    </InputField>
                  </Section>
                  <div className="mt-2">
                    <SubmitButton
                      disabled={loading || !customerValid}
                      loading={loading}
                      type="submit"
                    >
                      {t('referral_form.submit')}
                    </SubmitButton>
                  </div>
                  {error && (
                    <ErrorMessage>{t('errors.technical')}</ErrorMessage>
                  )}
                </>
              )}
            </form>
          )}
        </Form>
        {customerFormState === FormState.COLLAPSED && !isForced && (
          <div className="fab-container">
            <PrimaryButton onClick={setBookingFlow}>
              {t('main_form.buttons.booking_flow')}
            </PrimaryButton>
          </div>
        )}
      </ModuleSection>
    </>
  );
}
